import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';

export interface PagoCuentaCorrientePayload {
  p_session_id: string;
  p_branch_id: string;
  p_client_id: string;
  p_amount: number;
  p_payment_method_id: string;
  p_description: string;
  p_sale_id?: string | null;
}

export interface HistorialCuentaCorriente {
  deudas: any[];
  creditos: any[];
}

export interface MovimientoCuentaCorriente {
  id: string;
  client_id: string;
  business_id: string;
  fecha: string;
  tipo: 'debito' | 'credito';
  monto: number;
  concepto: string;
}

/** Tipo literal para la clasificación semántica de movimientos en el historial completo */
export type FuenteMovimiento = 'venta' | 'abono_libre' | 'cobro_venta' | 'devolucion' | 'split_remanente' | 'split_excedente';

/** Interfaz denormalizada para la vista v_client_cc_history con Running Balance */
export interface MovimientoHistorial {
  id: string;
  client_id: string;
  business_id: string;
  fecha: string;
  tipo: 'debito' | 'credito';
  monto: number;
  concepto: string;
  sale_status: string | null;
  fuente: FuenteMovimiento;
  sale_ref_id: string | null;
  saldo_acumulado: number;
}

@Injectable({ providedIn: 'root' })
export class CuentaCorrienteService {
  private supabase = inject(SupabaseService).client;

  /**
   * Consulta asíncrona de saldos y créditos en paralelo.
   * Diseñado para ser consumido por la API resource() de Angular.
   */
  async obtenerHistorial(clientId: string): Promise<HistorialCuentaCorriente> {
    const [salesReq, cashReq] = await Promise.all([
      this.supabase
        .from('sales')
        .select('*')
        .eq('client_id', clientId)
        .eq('status', 'pending'),
      this.supabase
        .from('cash_transactions')
        .select('*')
        .eq('client_id', clientId)
        .is('sale_id', null)
    ]);

    if (salesReq.error) throw salesReq.error;
    if (cashReq.error) throw cashReq.error;

    return {
      deudas: salesReq.data || [],
      creditos: cashReq.data || []
    };
  }

  /**
   * Consulta paginada y filtrada de movimientos unificados de cuenta corriente.
   * Utiliza la vista v_client_cc_movements en la base de datos.
   */
  async obtenerMovimientosPaginados(params: {
    clientId: string;
    search?: string;
    type?: 'all' | 'income' | 'expense' | null;
    startDate?: Date | null;
    endDate?: Date | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<{ data: MovimientoCuentaCorriente[]; total: number }> {
    let query = this.supabase
      .from('v_client_cc_movements' as any)
      .select('*', { count: 'exact' });

    query = query.eq('client_id', params.clientId);

    // Aplicar Filtro de Tipo (income -> credito, expense -> debito)
    if (params.type === 'income') {
      query = query.eq('tipo', 'credito');
    } else if (params.type === 'expense') {
      query = query.eq('tipo', 'debito');
    }

    // Aplicar Filtro de Búsqueda (concepto o id)
    if (params.search) {
      const term = `%${params.search}%`;
      query = query.or(`concepto.ilike.${term},id.ilike.${term}`);
    }

    // Aplicar Filtro de Fechas
    if (params.startDate) {
      const start = new Date(params.startDate);
      start.setHours(0, 0, 0, 0);
      query = query.gte('fecha', start.toISOString());
    }
    if (params.endDate) {
      const end = new Date(params.endDate);
      end.setHours(23, 59, 59, 999);
      query = query.lte('fecha', end.toISOString());
    }

    // Paginación
    const fromIdx = params.pageIndex * params.pageSize;
    const toIdx = fromIdx + params.pageSize - 1;
    query = query.range(fromIdx, toIdx);

    // Ordenar por fecha descendente
    const { data, error, count } = await query.order('fecha', { ascending: false });

    if (error) {
      console.error('Error al obtener movimientos paginados:', error);
      throw error;
    }

    return {
      data: (data as any || []) as MovimientoCuentaCorriente[],
      total: count || 0
    };
  }

  /**
   * Consulta paginada del historial completo (irrestricto) de cuenta corriente.
   * Utiliza la vista v_client_cc_history con Window Function para saldo acumulado.
   * La ordenación es ASC por diseño para que el running balance sea matemáticamente coherente.
   */
  async obtenerHistorialCompleto(params: {
    clientId: string;
    type?: 'all' | 'income' | 'expense' | null;
    startDate?: Date | null;
    endDate?: Date | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<{ data: MovimientoHistorial[]; total: number }> {
    let query = this.supabase
      .from('v_client_cc_history' as any)
      .select('*', { count: 'exact' });

    query = query.eq('client_id', params.clientId);

    // Filtro de tipo (income → credito, expense → debito)
    if (params.type === 'income') {
      query = query.eq('tipo', 'credito');
    } else if (params.type === 'expense') {
      query = query.eq('tipo', 'debito');
    }

    // Filtro de fechas
    if (params.startDate) {
      const start = new Date(params.startDate);
      start.setHours(0, 0, 0, 0);
      query = query.gte('fecha', start.toISOString());
    }
    if (params.endDate) {
      const end = new Date(params.endDate);
      end.setHours(23, 59, 59, 999);
      query = query.lte('fecha', end.toISOString());
    }

    // Paginación server-side
    const fromIdx = params.pageIndex * params.pageSize;
    const toIdx = fromIdx + params.pageSize - 1;
    query = query.range(fromIdx, toIdx);

    // Ordenación ASC requerida por la Window Function (tie-breaking por id)
    const { data, error, count } = await query
      .order('fecha', { ascending: true })
      .order('id', { ascending: true });

    if (error) {
      console.error('Error al obtener historial completo de CC:', error);
      throw error;
    }

    return {
      data: (data as any[] || []) as MovimientoHistorial[],
      total: count || 0
    };
  }

  /**
   * Indica si la empresa tiene al menos una cuenta con saldo distinto de cero (deuda o saldo a favor).
   * Mismo criterio que el listado de C.C.; el conteo se resuelve en la base, sin traer filas.
   */
  async hayCuentasConSaldo(businessId: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from('v_clients_cc_balance' as any)
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .neq('saldo_neto', 0);

    if (error) throw error;
    return (count ?? 0) > 0;
  }

  /**
   * Consulta el balance consolidado (saldo neto, deudas, créditos) de un cliente específico.
   * Utiliza la vista v_clients_cc_balance.
   */
  async obtenerBalanceCliente(clientId: string): Promise<{ totalDeudas: number; totalCreditos: number; saldoNeto: number; cantidadMovimientos: number } | null> {
    const { data, error } = await this.supabase
      .from('v_clients_cc_balance' as any)
      .select('total_deudas, total_creditos, saldo_neto, cantidad_movimientos')
      .eq('id', clientId)
      .maybeSingle();

    if (error) {
      console.error('Error al obtener balance de cliente:', error);
      throw error;
    }

    if (!data) return null;

    return {
      totalDeudas: (data as any).total_deudas || 0,
      totalCreditos: (data as any).total_creditos || 0,
      saldoNeto: (data as any).saldo_neto || 0,
      cantidadMovimientos: (data as any).cantidad_movimientos || 0
    };
  }

  /**
   * Consulta consolidada de saldos (Deudas vs Créditos) para todos los clientes de una empresa con filtros y paginación.
   */
  async obtenerSaldosConsolidados(params: {
    businessId: string;
    search?: string;
    pageIndex?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number }> {
    let query = this.supabase
      .from('v_clients_cc_balance' as any)
      .select('*', { count: 'exact' })
      .eq('business_id', params.businessId)
      .neq('saldo_neto', 0);

    if (params.search) {
      const term = `%${params.search}%`;
      query = query.or(`full_name.ilike.${term},email.ilike.${term},phone.ilike.${term}`);
    }

    if (params.pageIndex !== undefined && params.pageSize !== undefined) {
      const fromIdx = params.pageIndex * params.pageSize;
      const toIdx = fromIdx + params.pageSize - 1;
      query = query.range(fromIdx, toIdx);
    }

    const { data, error, count } = await query.order('full_name');

    if (error) {
      console.error('Error al obtener saldos consolidados:', error);
      return { data: [], total: 0 };
    }

    const mapped = (data || []).map((c: any) => ({
      id: c.id,
      full_name: c.full_name,
      email: c.email,
      phone: c.phone,
      totalDeudas: c.total_deudas,
      totalCreditos: c.total_creditos,
      saldoNeto: c.saldo_neto,
      saldo_neto: c.saldo_neto,
      cantidad_movimientos: c.cantidad_movimientos
    }));

    return {
      data: mapped,
      total: count || 0
    };
  }

  /**
   * Invoca la RPC segura para procesar un pago de C.C.
   */
  async registrarPago(payload: PagoCuentaCorrientePayload) {
    const { data, error } = await (this.supabase as any).rpc('procesar_pago_cuenta_corriente', payload);
    if (error) throw error;
    return data;
  }
}
